import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  Divider,
  Link as MuiLink,
} from "@mui/material";
import {
  WarningAmberRounded as WarningIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { colors, customShadows, borderRadius, borderRadiusPx } from "../../../../theme";
import { CommonButton } from "../../ui";

/**
 * SummaryActionsCard - Summarizes performance metrics, presents quick actions, and compliance notices.
 */
const SummaryActionsCard = ({
  title = "",
  tooltip = "",
  stats = null,
  statementLinkText = "",
  onStatementClick,
  statementLinkTo,
  quickActions = [],
  noticeTitle = "",
  noticeMessage = "",
  currencyPrefix = "",
  currencySuffix = "",
  sx = {},
}) => {
  const ab = colors.dashboard.alertBox;

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: borderRadiusPx.card,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardSubtle,
        backgroundColor: colors.dashboard.cardBg,
        ...sx,
      }}
    >
      <CardContent sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Top Summary Header */}
        {title && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.02rem",
              color: colors.dashboard.title,
              mb: 1.5,
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </Typography>
        )}

        {stats && (
          <Stack spacing={0.9} mb={1.5}>
            {stats.gamesCreated !== undefined && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ color: colors.slate[500], fontSize: "0.82rem" }}>
                  Games Created
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.82rem", color: colors.slate[900] }}>
                  {stats.gamesCreated}
                </Typography>
              </Stack>
            )}

            {stats.grossRevenue !== undefined && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ color: colors.slate[500], fontSize: "0.82rem" }}>
                  Gross Revenue
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.82rem", color: colors.slate[900] }}>
                  {currencyPrefix}{stats.grossRevenue}{currencySuffix}
                </Typography>
              </Stack>
            )}

            {stats.revenueTax !== undefined && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ color: colors.slate[500], fontSize: "0.82rem" }}>
                  Revenue Tax {stats.taxPercentage ? `(${stats.taxPercentage})` : ""}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.82rem", color: colors.slate[900] }}>
                  {currencyPrefix}{stats.revenueTax}{currencySuffix}
                </Typography>
              </Stack>
            )}

            {stats.netRevenue !== undefined && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ color: colors.slate[500], fontSize: "0.82rem" }}>
                  Net Revenue (After Tax)
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.82rem", color: colors.slate[900] }}>
                  {currencyPrefix}{stats.netRevenue}{currencySuffix}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}

        {statementLinkText && statementLinkTo ? (
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <MuiLink
              component={Link}
              to={statementLinkTo}
              sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
                color: colors.primary.main,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {statementLinkText}
            </MuiLink>
          </Box>
        ) : statementLinkText && onStatementClick ? (
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <MuiLink
              component="button"
              onClick={onStatementClick}
              sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
                color: colors.primary.main,
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {statementLinkText}
            </MuiLink>
          </Box>
        ) : null}

        {quickActions && quickActions.length > 0 && (
          <>
            <Divider sx={{ my: 1, borderColor: colors.dashboard.cardBorder }} />

            {/* Quick Actions Header */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, fontSize: "0.88rem", color: colors.slate[900], mt: 0.5, mb: 1.25 }}
            >
              Quick Actions
            </Typography>

            <Stack spacing={1} mb={2}>
              {quickActions.map((action, idx) => {
                const btnProps = action.path
                  ? { component: Link, to: action.path }
                  : { onClick: action.onClick };

                return (
                  <CommonButton
                    key={idx}
                    fullWidth
                    variant="action"
                    startIcon={action.icon}
                    {...btnProps}
                    sx={{
                      justifyContent: "flex-start",
                      px: 1.5,
                    }}
                  >
                    {action.label}
                  </CommonButton>
                );
              })}
            </Stack>
          </>
        )}

        {noticeMessage && (
          <Box
            sx={{
              mt: "auto",
              p: 1.75,
              borderRadius: borderRadiusPx.lg,
              backgroundColor: ab.background,
              border: `1px solid ${ab.border}`,
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" mb={0.5}>
              <WarningIcon sx={{ fontSize: "1rem", color: ab.icon }} />
              {noticeTitle && (
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, fontSize: "0.78rem", color: ab.title }}
                >
                  {noticeTitle}
                </Typography>
              )}
            </Stack>
            <Typography
              variant="caption"
              sx={{ color: ab.text, fontSize: "0.74rem", lineHeight: 1.45, display: "block" }}
            >
              {noticeMessage}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

SummaryActionsCard.propTypes = {
  title: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  stats: PropTypes.shape({
    gamesCreated: PropTypes.number,
    grossRevenue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    netRevenue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    taxPercentage: PropTypes.string,
  }),
  statementLinkText: PropTypes.string,
  onStatementClick: PropTypes.func,
  statementLinkTo: PropTypes.string,
  quickActions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      path: PropTypes.string,
      onClick: PropTypes.func,
    })
  ),
  noticeTitle: PropTypes.string,
  noticeMessage: PropTypes.node,
  currencyPrefix: PropTypes.string,
  currencySuffix: PropTypes.string,
  sx: PropTypes.object,
};

export default SummaryActionsCard;
